import React, { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import ServiceSection from "./ServiceSection";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import { Link } from "react-router-dom";

// Colorful theme backgrounds
import serviceBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

// Custom CSS for Service/Module cards - uses theme variables
const serviceModuleStyles = `
  .service-bg {
    background-image: url(${serviceBg});
  }
  .service-module-card {
    background-image: url(${cardBg});
    border-radius: 20px;
    height: 100%;
  }
  .service-module-icon-box {
    width: 60px;
    height: 60px;
    border-radius: 15px;
    font-size: 24px;
    color: var(--color-primary);
  }
  .service-module-ai-text {
    font-size: 14px;
  }
  .service-module-badge-addon {
    background: var(--color-primary);
    border-radius: 20px;
    font-size: 12px;
    color: var(--color-secondary);
  }
  .service-module-badge-free {
    background: var(--color-primary);
    border-radius: 20px;
    font-size: 12px;
    color: var(--color-secondary);
  }
  .service-module-badge-included {
    background: var(--color-primary);
    border-radius: 20px;
    font-size: 12px;
  }
  .service-module-tagline {
    color: var(--color-primary);
    font-weight: 500;
  }
  .service-module-desc {
    font-size: 14px;
    line-height: 1.6;
  }
  .service-module-feature-list {
    font-size: 13px;
  }
  .service-module-check-icon {
    color: var(--color-primary);
    font-size: 10px;
  }
  .service-module-feature-title {
    color: var(--color-primary);
  }
  .service-intro-text {
    font-size: 18px;
    line-height: 1.8;
  }
  .service-stat-number {
    color: var(--color-primary);
  }
  .service-highlight-badge {
    background: rgba(138,43,226,0.1);
    border-radius: 10px;
    font-size: 13px;
    color: #a78bfa;
  }
  .service-highlight-divider {
    border-top: 1px solid rgba(255,255,255,0.1);
  }
  .service-card-rounded {
    background-image: url(${cardBg});
    border-radius: 20px;
  }
  .service-api-tag {
    border-radius: 10px;
    font-size: 14px;
  }
  .service-icon-lg {
    font-size: 40px;
    color: var(--color-primary);
  }
  .service-text-sm {
    font-size: 14px;
  }
  .service-icon-box-sm {
    width: 45px;
    height: 45px;
    border-radius: 10px;
    font-size: 18px;
    flex-shrink: 0;
  }
  .service-icon-color {
    color: var(--color-primary);
  }
  .service-title-sm {
    font-size: 14px;
  }
  .service-desc-xs {
    font-size: 12px;
  }
  .service-cta-text {
    max-width: 600px;
    margin: 0 auto;
  }
  .service-btn-outline {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.2);
  }
`;

// Feature detail sections data
const moduleDetails = [
  {
    id: "link",
    icon: "fas fa-phone-volume",
    name: "LINK - Business Phone System",
    aiCharacter: "LINK",
    tagline: "Your Complete Telecom Department",
    description: "Every AXOIX subscription includes a built-in business phone system. No expensive PBX hardware, no complicated setup, no monthly fees for internal calls.",
    freeFeatures: [
      "Internal extensions: 3 / 10 / 15 / unlimited by plan",
      "Browser-based WebRTC calling",
      "Internal voicemail",
      "Call transfers between team members",
      "Presence/availability status",
      "No phone hardware required"
    ],
    paidFeatures: [
      "DID phone numbers (+91-XXX-XXXX)",
      "Professional IVR builder (Press 1 for Sales...)",
      "Call recording & storage",
      "Click-to-call from CRM",
      "Missed call alerts & notifications",
      "Call analytics & reporting"
    ],
    highlight: "FREE internal calling with all paid plans!"
  },
  {
    id: "accounting",
    icon: "fas fa-calculator",
    name: "Accounting & Finance",
    aiCharacter: "LEDGER",
    tagline: "Complete Financial Control",
    description: "LEDGER provides enterprise-grade double-entry accounting designed for Indian businesses. From GST compliance to multi-currency support — your finances are in expert hands.",
    features: [
      "Double-entry bookkeeping with chart of accounts",
      "Invoice generation with GST calculations",
      "Expense tracking & categorization",
      "Bank reconciliation",
      "GST return preparation (GSTR-1, GSTR-3B)",
      "TDS calculations & deductions",
      "Multi-currency support",
      "Profit & Loss statements",
      "Balance sheet generation",
      "Cash flow reports",
      "Accounts receivable/payable aging",
      "Budget planning & tracking"
    ],
    highlight: "Indian tax compliance built-in!"
  },
  {
    id: "hr",
    icon: "fas fa-users",
    name: "HR & Workforce Management",
    aiCharacter: "KEEPER",
    tagline: "Manage Your Most Valuable Asset",
    description: "KEEPER handles everything HR — from recruitment to retirement. Employee records, attendance, payroll preparation, and performance management in one unified system.",
    features: [
      "Complete employee database",
      "Document management (ID proofs, contracts)",
      "Attendance tracking (biometric integration ready)",
      "Leave management with approval workflows",
      "Payroll preparation & salary slips",
      "Statutory compliance (PF, ESI, PT)",
      "Recruitment pipeline management",
      "Job posting & application tracking",
      "Onboarding checklists",
      "Performance review cycles",
      "Training & certification tracking",
      "Exit management & full-and-final"
    ],
    highlight: "From hiring to retiring — one system!"
  },
  {
    id: "inventory",
    icon: "fas fa-boxes",
    name: "Inventory Management",
    aiCharacter: "HAUL",
    tagline: "Never Run Out of Stock",
    description: "HAUL ensures you always have what you need, when you need it. Real-time inventory tracking, automated reorder points, and multi-warehouse support.",
    features: [
      "Real-time stock levels",
      "Multi-warehouse management",
      "Barcode/QR code support",
      "Low-stock alerts & notifications",
      "Automated purchase order generation",
      "Supplier management",
      "Stock transfers between locations",
      "Batch & serial number tracking",
      "Expiry date management",
      "Stock valuation (FIFO, weighted average)",
      "Inventory audit & adjustment",
      "Stock movement reports"
    ],
    highlight: "Multi-warehouse with real-time tracking!"
  },
  {
    id: "projects",
    icon: "fas fa-project-diagram",
    name: "Project Management",
    aiCharacter: "FORGE",
    tagline: "Deliver Projects On Time",
    description: "FORGE keeps your projects on track with task management, team coordination, and progress tracking. Kanban boards, Gantt charts, and automated reporting.",
    features: [
      "Project creation with milestones",
      "Task assignment & dependencies",
      "Kanban board view",
      "Gantt chart timeline",
      "Time tracking & logging",
      "Resource allocation",
      "Team workload balancing",
      "Progress tracking & reporting",
      "File attachments & comments",
      "Client access portals",
      "Budget tracking per project",
      "Automated progress emails"
    ],
    highlight: "Kanban + Gantt + Time tracking!"
  },
  {
    id: "crm",
    icon: "fas fa-handshake",
    name: "CRM & Sales",
    aiCharacter: "BARTER",
    tagline: "Close More Deals",
    description: "BARTER manages your entire sales pipeline — from lead capture to deal closure. With click-to-call via LINK, your sales team is unstoppable.",
    features: [
      "Lead capture & management",
      "Lead scoring & prioritization",
      "Contact & company database",
      "Sales pipeline visualization",
      "Opportunity tracking",
      "Follow-up reminders",
      "Email integration",
      "Click-to-call via LINK",
      "Quote & proposal generation",
      "Sales forecasting",
      "Commission tracking",
      "Win/loss analysis"
    ],
    highlight: "Click-to-call with LINK integration!"
  },
  {
    id: "email",
    icon: "fas fa-envelope",
    name: "Email AI System",
    aiCharacter: "ECHO",
    tagline: "Professional Communication Made Easy",
    description: "ECHO powers your email with AI assistance — compose, reply, summarize, and categorize. Self-hosted email with custom domain support.",
    features: [
      "AI-powered email composition",
      "Smart reply suggestions",
      "Email summarization",
      "Automatic categorization",
      "Custom domain email (@yourcompany.com)",
      "Multi-user mailboxes",
      "2GB storage per user",
      "Email templates",
      "Signature management",
      "Spam filtering",
      "Email scheduling",
      "Read receipts"
    ],
    highlight: "AI writes your emails in seconds!"
  },
  {
    id: "compliance",
    icon: "fas fa-shield-alt",
    name: "Compliance & Legal",
    aiCharacter: "CIPHER",
    tagline: "Stay on the Right Side",
    description: "CIPHER monitors compliance requirements, tracks document expirations, and maintains audit trails. Never miss a renewal or regulatory deadline.",
    features: [
      "Document expiry tracking",
      "License & registration management",
      "Insurance policy tracking",
      "Regulatory deadline alerts",
      "Audit trail maintenance",
      "Compliance checklist templates",
      "Digital document storage",
      "Version control",
      "Access control & permissions",
      "Compliance reports",
      "Vendor compliance tracking",
      "Custom compliance workflows"
    ],
    highlight: "Never miss a deadline!"
  },
  {
    id: "analytics",
    icon: "fas fa-chart-line",
    name: "Analytics & Reporting",
    aiCharacter: "VARN",
    tagline: "Data-Driven Decisions",
    description: "VARN turns raw data into actionable insights. Real-time dashboards, custom reports, and trend analysis to help you make smarter business decisions.",
    features: [
      "Real-time dashboard widgets",
      "Custom report builder",
      "Sales analytics",
      "Financial metrics",
      "Inventory turnover reports",
      "Employee performance metrics",
      "Project profitability analysis",
      "Trend identification",
      "Forecasting models",
      "Export to PDF/Excel",
      "Scheduled report delivery",
      "Role-based report access"
    ],
    highlight: "AI-powered insights & forecasting!"
  },
  {
    id: "remedy",
    icon: "fas fa-heartbeat",
    name: "REMEDY - Complaint Management",
    aiCharacter: "REMEDY",
    tagline: "Resolution & Mediation for Your Disputes",
    description: "Turn complaints into opportunities. REMEDY manages your customer complaint lifecycle with SLA monitoring, escalation workflows, and complete resolution tracking. Never let a customer issue slip through the cracks.",
    features: [
      "Complaint ticket system",
      "Multi-channel intake (email, phone, web)",
      "Automatic ticket assignment",
      "SLA monitoring & alerts",
      "Escalation workflows",
      "Priority-based queuing",
      "Customer communication log",
      "Resolution tracking & verification",
      "Root cause analysis",
      "Satisfaction surveys",
      "Complaint analytics & trends",
      "Agent performance metrics"
    ],
    highlight: "FREE for ALL users - even Visitor plan!",
    freeForAll: true
  },
  {
    id: "seal",
    icon: "fas fa-stamp",
    name: "SEAL - E-Signature Module",
    aiCharacter: "SEAL",
    tagline: "Secure Electronic Agreement & Ledger",
    description: "SEAL handles all your digital signatures — quotes, contracts, NDAs, and agreements. Get documents signed faster, close deals quicker. Legally compliant under IT Act 2000 — available FREE to all users including Visitor plan.",
    features: [
      "E-signatures on quotes & proposals",
      "Contract signing workflow",
      "Multi-party signature support",
      "Document sharing & tracking",
      "Signature status notifications",
      "Automatic reminders",
      "Legal compliance (IT Act 2000)",
      "Audit trail for all signatures",
      "Template library",
      "Integration with CRM deals",
      "Signed document storage",
      "Expiry & renewal tracking"
    ],
    highlight: "FREE for ALL users - even Visitor plan!",
    freeForAll: true
  },
  {
    id: "tender",
    icon: "fas fa-file-contract",
    name: "Tender Management",
    aiCharacter: "PROBE",
    tagline: "For Government Contractors",
    description: "Complete NIT extraction, CPWD rates database, BOQ-based tender quotes, and auto-project creation on winning. Built specifically for government contractors.",
    features: [
      "NIT extractor (67+ fields)",
      "CPWD DSR rate database",
      "BOQ-based tender quoting",
      "Rate analysis templates",
      "Tender submission tracking",
      "EMD/SD management",
      "Auto-project creation on winning",
      "RA Bill generation",
      "Measurement book entries",
      "Running account tracking",
      "Tender deadline alerts",
      "Historical rate comparison"
    ],
    highlight: "Specialty addon for govt contractors!",
    addon: true
  },
  {
    id: "upi-payments",
    icon: "fas fa-qrcode",
    name: "UPI Payment Collection",
    aiCharacter: "LEDGER",
    tagline: "Zero Gateway Fees",
    description: "Collect payments directly from customers via UPI with zero transaction fees. Every invoice auto-generates a BharatQR-compatible QR code. Customers scan with any UPI app — GPay, PhonePe, Paytm — and pay instantly. No Razorpay or Stripe middleman fees.",
    features: [
      "Auto-generated BharatQR code on every invoice",
      "One-tap deep links for GPay, PhonePe, Paytm",
      "Public payment page (no customer login needed)",
      "UTR-based payment confirmation",
      "Duplicate UTR detection (anti-fraud)",
      "Invoice auto-updates on payment",
      "Configure your own UPI ID (business@upi)",
      "Multi-gateway support (UPI + Razorpay + Stripe)",
      "Works across all 19 industry verticals",
      "Payment history & reconciliation reports",
      "Secure — sensitive credentials never exposed",
      "Zero setup cost, zero transaction fees"
    ],
    highlight: "Zero fees — money goes straight to your UPI!",
    freeForAll: true
  },
  {
    id: "chorus",
    icon: "fas fa-share-alt",
    name: "CHORUS - Social Media Studio",
    aiCharacter: "CHORUS",
    tagline: "32 Screens — Enterprise Social Media Suite",
    description: "Post across 7 platforms including YouTube, manage Meta Ads and Google Ads, run WhatsApp campaigns, build AI auto-responders, sync catalogs to Instagram Shopping, capture leads from DMs, and orchestrate AI agents that create content autonomously.",
    features: [
      "Multi-platform posting (Facebook, Instagram, LinkedIn, Twitter/X, Threads, YouTube)",
      "YouTube Studio — upload, thumbnails, playlists and comment moderation",
      "Google Business Profile — manage your listing from the same dashboard",
      "Unified Messenger inbox across Meta platforms",
      "Meta Ads dashboard — create, optimize & track campaigns",
      "Google Ads — campaigns, keywords, budgets, bids & conversion tracking",
      "WhatsApp Business templates with Meta approval workflow",
      "AI Auto-Responses with sentiment analysis",
      "Instagram Shopping — shoppable posts & catalog sync",
      "Agent OS — AI agent orchestration",
      "Knowledge Brain — custom RAG knowledge base",
      "Voice Studio & Podcast Studio",
      "Lead Forms builder & Fundraiser management",
      "Lead & complaint detection — auto-routes to CRM & REMEDY",
      "Engagement analytics with trend tracking"
    ],
    highlight: "14,000+ lines of code — the most powerful social suite in any ERP!"
  },
  {
    id: "connect",
    icon: "fas fa-comments",
    name: "CONNECT - Team Chat & Video",
    aiCharacter: "ECHO",
    tagline: "Slack + Zoom Built Into Your Dashboard",
    description: "Channels, direct messages, threaded conversations, voice huddles, and Jitsi-powered video meetings with screen sharing — all inside AXOIX with zero extra subscriptions.",
    features: [
      "Organized channels by department, project, or topic",
      "Direct messages (1-on-1 and group)",
      "Threaded conversations for focused discussions",
      "Jitsi-powered video meetings (no extra app needed)",
      "Screen sharing in video calls",
      "Voice huddles for quick stand-ups",
      "Real-time WebSocket messaging",
      "Meeting invitations with calendar integration",
      "Online/offline presence indicators",
      "@mentions with push notifications",
      "File & document sharing with preview",
      "Integrates with FORGE project channels"
    ],
    highlight: "Replace Slack + Zoom — zero extra cost!"
  },
  {
    id: "costing",
    icon: "fas fa-coins",
    name: "AI Costing Engine",
    aiCharacter: "FORGE",
    tagline: "Estimate Costs in Seconds — Not Days",
    description: "Accept a project description in text, upload an image/floor plan, or pick a template — get a detailed cost breakdown with material, labour, and overhead line items. Multi-industry estimation.",
    features: [
      "Text-based estimation — describe project, get itemized costs",
      "Image/diagram analysis — upload floor plans or site photos",
      "Template-based estimation with reusable templates",
      "Material, labour & overhead cost breakdowns",
      "Margin & discount calculation with tax support",
      "Material cost rates database",
      "Multi-industry (construction, manufacturing, events)",
      "Floor plan generation from descriptions",
      "Export to PDF with company branding",
      "Feeds into quotations, tenders & accounting",
      "AI-powered — learns from estimation history",
      "Manual mode for custom line items"
    ],
    highlight: "3 AI modes: Text, Image & Template estimation!"
  },
  {
    id: "storefront",
    icon: "fas fa-globe",
    name: "Website Builder & Storefront",
    aiCharacter: "FORGE",
    tagline: "Your Business Online in Minutes",
    description: "Every AXOIX vertical includes a public-facing storefront. Drag-and-drop section builder with multiple layout variants, custom domain support, and real-time sync with your dashboard data.",
    features: [
      "Drag-and-drop section builder",
      "Multiple layout variants (Hero, About, Gallery, FAQ, Pricing, Team)",
      "Custom domain support (yourbusiness.com)",
      "Real-time sync with dashboard data",
      "Online booking (hotel, salon, gym, healthcare)",
      "Online menu (restaurant)",
      "Property listings (real estate)",
      "Vehicle showcase (car & bike dealers)",
      "SEO tools (meta, sitemap, robots.txt)",
      "Contact forms & WhatsApp chat widget",
      "Google Analytics & Pixel integration",
      "SSL certificate included"
    ],
    highlight: "No separate website needed — 14 industry storefronts!"
  },
  {
    id: "barter",
    icon: "fas fa-store",
    name: "Barter Marketplace",
    aiCharacter: "BARTER",
    tagline: "Multi-Shop P2P Exchange & Commerce",
    description: "Create multiple shops, list products or services, and participate in a peer-to-peer exchange network. Built-in referral system, order management, and seller analytics.",
    features: [
      "Multi-shop support per tenant",
      "Product & service listings with images",
      "Order management with status tracking",
      "Seller dashboard with analytics",
      "Auto-shop creation on signup",
      "Referral system with tracking",
      "Category management",
      "Search & discovery across marketplace",
      "Seller ratings & reviews",
      "Order notifications",
      "Revenue analytics per shop",
      "Integration with main inventory"
    ],
    highlight: "Run multiple shops from one dashboard!"
  }
];

const ServicePage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <SEO title="Services & Modules" description="AXOIX platform modules — HR, Finance, CRM, Inventory, Projects, Communications, AI Costing Engine, LINK Phone System, and more." path="/service" />
      <style>{serviceModuleStyles}</style>
      <div className='about-page inner-page'>
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle={t('services.pageTitle')} pagesub={t('services.pageSub')} />

            {/* Intro Section */}
            <section
              className="pt-100 pb-50 bg_img service-bg"
            >
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-lg-10 text-center">
                    <div className="sec-title sec-title--two mb-20">
                      <span className="sub-title mb-20">
                        <img src={subLeftIcon} alt="icon" /> Powerful Modules{" "}
                        <img src={subRightIcon} alt="icon" />
                      </span>
                      <h2 className="title">{t('services.introTitle')}</h2>
                    </div>
                    <p className="opacity-75 mb-30 service-intro-text">
                      {t('services.introDescription')}
                    </p>
                    <div className="d-flex justify-content-center gap-4 flex-wrap">
                      <div className="text-center">
                        <h3 className="mb-0 service-stat-number">{t('about.stats.characters')}</h3>
                        <span className="opacity-75">{t('about.stats.charactersLabel')}</span>
                      </div>
                      <div className="text-center">
                        <h3 className="mb-0 service-stat-number">{t('about.stats.languages')}</h3>
                        <span className="opacity-75">{t('about.stats.languagesLabel')}</span>
                      </div>
                      <div className="text-center">
                        <h3 className="mb-0 service-stat-number">{t('about.stats.commands')}</h3>
                        <span className="opacity-75">{t('about.stats.commandsLabel')}</span>
                      </div>
                      <div className="text-center">
                        <h3 className="mb-0 service-stat-number">{t('about.stats.integrations')}</h3>
                        <span className="opacity-75">{t('about.stats.integrationsLabel')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Overview Section */}
            <ServiceSection />

            {/* Detailed Module Cards */}
            <section
              className="pt-100 pb-100 bg_img service-bg"
            >
              <div className="container">
                <div className="sec-title sec-title--two text-center mb-60">
                  <span className="sub-title mb-20">
                    <img src={subLeftIcon} alt="icon" /> Detailed Features{" "}
                    <img src={subRightIcon} alt="icon" />
                  </span>
                  <h2 className="title">Explore Each Module</h2>
                  <p className="opacity-75 mt-15">Click on any module to learn more about its capabilities</p>
                </div>

                <div className="row">
                  {moduleDetails.map((module) => (
                    <div key={module.id} className="col-lg-6 mb-40">
                      <div className="pricing-item xb-border bg_img p-30 service-module-card">
                        {/* Module Header */}
                        <div className="d-flex align-items-center mb-25">
                          <div className="xb-border d-flex align-items-center justify-content-center me-20 service-module-icon-box">
                            <i className={module.icon}></i>
                          </div>
                          <div>
                            <h4 className="mb-5">{module.name}</h4>
                            <span className="opacity-50 service-module-ai-text">
                              Powered by {module.aiCharacter}
                            </span>
                          </div>
                          {module.addon && (
                            <span className="ms-auto px-3 py-1 service-module-badge-addon">
                              Addon
                            </span>
                          )}
                          {module.freeForAll && (
                            <span className="ms-auto px-3 py-1 service-module-badge-free">
                              FREE for All
                            </span>
                          )}
                          {module.included && (
                            <span className="ms-auto px-3 py-1 service-module-badge-included">
                              Included
                            </span>
                          )}
                        </div>

                        {/* Tagline */}
                        <p className="mb-15 service-module-tagline">
                          {module.tagline}
                        </p>

                        {/* Description */}
                        <p className="opacity-75 mb-20 service-module-desc">
                          {module.description}
                        </p>

                        {/* LINK has special free/paid features */}
                        {module.id === 'link' ? (
                          <div className="row">
                            <div className="col-md-6">
                              <h6 className="mb-10 service-module-feature-title">
                                <i className="fas fa-check-circle me-2"></i>FREE Features
                              </h6>
                              <ul className="list-unstyled service-module-feature-list">
                                {module.freeFeatures?.map((feature, idx) => (
                                  <li key={idx} className="mb-1 opacity-75">
                                    <i className="fas fa-check me-2 service-module-check-icon"></i>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="col-md-6">
                              <h6 className="mb-10 service-module-feature-title">
                                <i className="fas fa-star me-2"></i>Premium Features
                              </h6>
                              <ul className="list-unstyled service-module-feature-list">
                                {module.paidFeatures?.map((feature, idx) => (
                                  <li key={idx} className="mb-1 opacity-75">
                                    <i className="fas fa-star me-2 service-module-check-icon"></i>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <div className="row">
                            {module.features?.slice(0, 12).map((feature, idx) => (
                              <div key={idx} className="col-md-6">
                                <p className="mb-2 opacity-75 service-module-feature-list">
                                  <i className="fas fa-check me-2 service-module-check-icon"></i>
                                  {feature}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Highlight Badge */}
                        <div className="mt-20 pt-15 service-highlight-divider">
                          <span className="px-3 py-2 d-inline-block service-highlight-badge">
                            <i className="fas fa-bolt me-2"></i>
                            {module.highlight}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* API Integrations Section */}
            <section className="pb-100">
              <div className="container">
                <div
                  className="pricing-item xb-border bg_img p-40 service-card-rounded"
                >
                  <div className="row align-items-center">
                    <div className="col-lg-6">
                      <span className="sub-title mb-15 d-block">Pre-Built Integrations</span>
                      <h3 className="mb-20">150+ API Integrations Ready to Use</h3>
                      <p className="opacity-75 mb-20">
                        AXOIX comes with pre-integrated APIs for Google (Gmail, Calendar, Drive, Docs, Sheets, Google Ads, Analytics 4, YouTube, Business Profile and Classroom), payments (UPI, Razorpay, Stripe), messaging (MSG91, Twilio, WhatsApp), travel (Amadeus GDS), accounting (Tally, QuickBooks), weather, news, currency, flights, railways, maps, stocks, AI/ML, and government data (Aadhaar, PAN, GST verification). 150+ BYOK providers ready to use.
                      </p>
                      <p className="opacity-75 mb-20">
                        Officer and Commander plans also include custom API access for your own integrations via REST APIs and webhooks.
                      </p>
                      <Link to="/feature/google-workspace" className="thm-btn agency-btn" style={{ display: 'inline-block' }}>
                        <span className="text">Explore the Google modules</span>
                      </Link>
                    </div>
                    <div className="col-lg-6">
                      <div className="d-flex flex-wrap gap-3 justify-content-center">
                        {[
                          'Gmail', 'Google Calendar', 'Google Drive', 'Google Ads', 'Analytics 4',
                          'YouTube', 'Google Classroom', 'UPI Payments', 'Razorpay', 'Stripe',
                          'WhatsApp', 'Amadeus GDS', 'Tally', 'AI/ML', 'Govt Data'
                        ].map((api, idx) => (
                          <span
                            key={idx}
                            className="xb-border px-3 py-2 service-api-tag"
                          >
                            {api}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Security & Safety Standards Section */}
            <section className="pb-100">
              <div className="container">
                <div
                  className="pricing-item xb-border bg_img p-40 service-card-rounded"
                >
                  <div className="row align-items-center">
                    <div className="col-lg-5 mb-30 mb-lg-0">
                      <span className="sub-title mb-15 d-block">5 Layers of Defense</span>
                      <h3 className="mb-20">Fortress-Grade Infrastructure</h3>
                      <p className="opacity-75 mb-20">
                        Your dashboard is protected by 5 layers of active defense — from network firewall to application-layer encryption. SSH is key-only, HSTS is preloaded in all major browsers, and automatic security patching keeps everything current.
                      </p>
                      <div className="d-flex align-items-center gap-3">
                        <i className="fas fa-shield-alt service-icon-lg"></i>
                        <span className="service-text-sm opacity-75">
                          HSTS preloaded · Full CSP · SSH key-only · Auto-patching enabled
                        </span>
                      </div>
                    </div>
                    <div className="col-lg-7">
                      <div className="row">
                        {[
                          { icon: 'fas fa-fire', title: 'iptables Firewall', desc: 'Default-deny policy — only explicitly allowed ports open' },
                          { icon: 'fas fa-globe', title: 'CrowdSec Intelligence', desc: 'Collaborative blocklists shared across 100K+ servers' },
                          { icon: 'fas fa-shield-alt', title: 'ModSecurity WAF', desc: '1,432 OWASP rules blocking SQLi & XSS in real-time' },
                          { icon: 'fas fa-ban', title: 'Fail2ban (8 Jails)', desc: 'Auto-bans brute-force attackers across SSH, HTTP & more' },
                          { icon: 'fas fa-tachometer-alt', title: 'Nginx Rate Limiting', desc: 'Request throttling prevents abuse and DDoS attempts' },
                          { icon: 'fas fa-database', title: 'Data Encryption', desc: 'MySQL TDE + encrypted binlogs + AES on PII fields' },
                        ].map((item, idx) => (
                          <div key={idx} className="col-md-6 mb-20">
                            <div className="d-flex align-items-start gap-3">
                              <div className="xb-border d-flex align-items-center justify-content-center service-icon-box-sm">
                                <i className={item.icon + " service-icon-color"}></i>
                              </div>
                              <div>
                                <h6 className="mb-1 service-title-sm">{item.title}</h6>
                                <p className="mb-0 opacity-50 service-desc-xs">{item.desc}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="pb-100">
              <div className="container">
                <div className="text-center">
                  <div
                    className="pricing-item xb-border bg_img p-50 service-card-rounded"
                  >
                    <h2 className="mb-15">Ready to Transform Your Business?</h2>
                    <p className="opacity-75 mb-30 service-cta-text">
                      Join thousands of businesses using AXOIX to automate operations, improve efficiency, and grow faster. Sign up for the free Explorer plan and experience the full suite.
                    </p>
                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                      <Link to="/signup" className="thm-btn agency-btn">
                        <span className="text">Join Now</span>
                        <span className="arrow">
                          <span className="arrow-icon">
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                              <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                              <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                              <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                              <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                              <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                              <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                            </svg>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                              <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                              <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                              <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                              <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                              <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                              <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                            </svg>
                          </span>
                        </span>
                      </Link>
                      <Link to="/pricing" className="thm-btn agency-btn service-btn-outline">
                        <span className="text">View Pricing</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ServicePage;
